import { useEffect, useMemo, useState } from "react"
import services from "../../../services/services";
import _ from "underscore";
import { decryptData, encryptData } from "../../../utils/encrypt";
import Highcharts from 'highcharts';
import Heatmap from 'highcharts/modules/heatmap';
Heatmap(Highcharts);

export const useMemoryHitUptime = ({ widget, projectChanged }) => {

    const [loader, setLoader] = useState(false);
    const [filterValues, setFilterValues] = useState({ service: [], server: [] });
    const [popupData, setPopupData] = useState([]);
    const [filterCount, setFilterCount] = useState(0);
    const [showFilter, setShowFilter] = useState(false);
    const [showPopup, setShowPopup] = useState(false);
    const [apiData, setApiData] = useState([]);
    const [expand, setExpand] = useState(false);
    const [allMemoryOptions, setAllMemoryOptions] = useState([]);
    const [allUptimeOptions, setAllUptimeOptions] = useState([]);
    const [serverWiseData, setServerWiseData] = useState({ pane: [], series: [], api_data: [] });
    const [serverWisePopup, setServerWisePopup] = useState({ show: false, data: [] });
    const [gaugeLoader, setGaugeLoader] = useState(false);
    const [intervalFlag, setIntervalFlag] = useState(false);

    useEffect(() => {
        getData();
    }, [projectChanged])

    useEffect(() => {
        widget === "memory" && getServerWiseData();
    }, [projectChanged, widget])

    useEffect(() => {
        const interval = setInterval(() => {
            getData();
            widget === "memory" && getServerWiseData();
        }, 120000);

        return () => clearInterval(interval);
    }, [intervalFlag]);

    useEffect(() => {
        formatData(widget, apiData);
    }, [apiData])

    const getData = async () => {
        try {
            const selectedProject = sessionStorage.getItem("inspectra_project") ? JSON.parse(decryptData(sessionStorage.getItem("inspectra_project"))) : {};
            if (!_.isEmpty(selectedProject)) {
                setLoader(true);
                const filterSessionKey = widget === "memory" ? "memoryfilters" : "uptimefilters"
                const filters = JSON.parse(decryptData(sessionStorage.getItem(filterSessionKey)) || "{}");
                const payload = {
                    type: 'service',
                    server: _.isEmpty(filters?.server) ? undefined : filters?.server?.toString(),
                    service_name: _.isEmpty(filters?.service) ? undefined : filters?.service?.toString(),
                    project_name: selectedProject?.project_key
                }
                const response = await services.callInspectra("GET", '/elsinspectra/top', payload);
                if (response) {
                    const data = response?.data;
                    setApiData(data);
                    setLoader(false);
                }
            }
        } catch (error) {
            console.error("Error getting Server Error Data: ", error);
            setLoader(false);
        }
    }

    const formatData = (widget, apiData) => {
        let allOptions = [];
        let allUptimeOpts = [];
        const serverGroups = _.uniq(_.pluck(apiData, "server_group"));
        let groupedData = serverGroups.reduce((acc, group) => {
            const filteredData = _.filter(apiData, { server_group: group });
            acc[group] = filteredData;
            return acc;
        }, {});

        groupedData = Object.fromEntries(
            Object.entries(groupedData).sort(([, a], [, b]) => b.length - a.length)
        );

        Object?.keys(groupedData)?.forEach((group) => {
            const filteredData = _.uniq(groupedData?.[group], item => `${item.server}-${item.service_name}`);
            const servers = _.uniq(_.pluck(filteredData, "server"));
            const services = _.uniq(_.pluck(filteredData, "service_name"));
            if (widget === "memory") {
                const data = formatHeatmapData(filteredData, servers, services);
                const option = generateMemoryOptions(servers, services, data, group, filteredData);
                allOptions.push(option);
            } else {
                let yAxis = [];
                const uptimeServices = _.uniq(_.pluck(groupedData[group], "service_name"));
                const servers = _.uniq(_.pluck(groupedData[group], "server"));
                servers?.forEach((server) => {
                    let obj = {
                        name: server,
                        data: []
                    }
                    uptimeServices?.forEach((service) => {
                        const findData = _.findWhere(groupedData[group], { service_name: service, server: server });
                        obj.data.push(parseFloat(findData?.uptime_mins) || 0);
                    })
                    yAxis.push(obj);
                })

                const combineData = _.pluck(yAxis, "data");
                const flatData = combineData?.flat();
                const options = generateUptimeOptions(uptimeServices, yAxis, Math.max(...flatData), group, filteredData);
                allUptimeOpts.push(options);
            }
        })
        setAllUptimeOptions(allUptimeOpts);
        setAllMemoryOptions(allOptions);
    }

    const formatHeatmapData = (filteredData, servers, services) => {

        const serverIndexMap = servers.reduce((acc, server, i) => {
            acc[server] = i;
            return acc;
        }, {});

        const serviceIndexMap = services.reduce((acc, service, i) => {
            acc[service] = i;
            return acc;
        }, {});

        const heatmapData = [];

        servers.forEach((server) => {
            services.forEach((service) => {
                const findData = _.findWhere(filteredData, { server: server, service_name: service });

                let memory = 0;
                if (!_.isEmpty(findData) && findData?.memory_usage) {
                    if (findData?.memory_usage.includes("M")) {
                        memory = parseFloat(findData?.memory_usage.split("/")[0].trim().replace("M", "")) || 0;
                    } else if (findData?.memory_usage.includes("G")) {
                        memory = parseFloat(findData?.memory_usage.split("/")[0].trim().replace("G", "")) * 1024 || 0;
                    }
                }

                heatmapData.push([
                    serverIndexMap[server],
                    serviceIndexMap[service],
                    memory
                ]);
            });
        });

        return heatmapData;
    }

    const generateMemoryOptions = (x_axis, y_axis, series, group, api_data) => {
        return {
            credits: { enabled: false },
            chart: {
                type: 'heatmap',
                plotBorderWidth: 1,
                height: y_axis?.length >= 5 ? Math.max(290, y_axis?.length * 20) : 180,
                width: x_axis?.length > 2 ? Math.max(230, x_axis?.length * 20) : 180,
                events: {
                    render: function () {
                        const chart = this;
                        chart.xAxis[0].ticks &&
                            Object.values(chart.xAxis[0].ticks).forEach(tick => {
                                if (tick.label && !tick.label.clickAttached) {
                                    tick.label.element.style.cursor = 'pointer';
                                    const categoryName = tick.label.textStr;
                                    tick.label.element.onclick = () => {
                                        handleShowDetailsPopupMemory(categoryName, "server", api_data);
                                    };
                                    tick.label.clickAttached = true;
                                }
                            });

                        chart.yAxis[0].ticks &&
                            Object.values(chart.yAxis[0].ticks).forEach((tick, index) => {
                                if (tick.label && !tick.label.clickAttached) {
                                    tick.label.element.style.cursor = 'pointer';
                                    const categoryName = y_axis[index];
                                    tick.label.element.onclick = () => {
                                        handleShowDetailsPopupMemory(categoryName, "service", api_data);
                                    };
                                    tick.label.clickAttached = true;
                                }
                            });
                    }
                }
            },
            title: {
                text: `<span class="px-2">${group}</span>`,
                useHTML: true,
                style: {
                    fontSize: '16px',
                    color: '#333',
                    backgroundColor: "#e5e5e5",
                    whiteSpace: 'nowrap',
                },
            },
            xAxis:
            {
                categories: x_axis,
                min: 0,
                max: x_axis.length - 1,
                labels: {
                    step: 1,
                    rotation: -45,
                    style: {
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                        fontSize: '12px'
                    }
                }
            },
            yAxis: {
                categories: y_axis,
                title: null,
                reversed: true,
                min: 0,
                max: y_axis.length - 1,
                labels: {
                    useHTML: true,
                    formatter: function () {
                        const fullLabel = this.value;
                        return `<span style="display: inline-block; max-width: 50px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; vertical-align: middle; " title="${fullLabel}">${fullLabel}</span>`;
                    },
                    style: {
                        fontSize: '12px',
                        cursor: 'pointer'
                    }
                }
            },
            colorAxis: {
                min: 0,
                stops: [
                    [0, '#0000FF'],
                    [0.3, '#4169E1'],
                    [0.5, '#c5e3ff'],
                    [0.7, '#F08080'],
                    [1, '#EF3A3A'],
                ],
            },
            legend: {
                align: 'right',
                layout: 'vertical',
                margin: 0,
                verticalAlign: 'top',
                y: 25,
                symbolHeight: Math.min(210, y_axis?.length * 20),
            },
            tooltip: {
                outside: true,
                style: {
                    zIndex: 9999
                },
                formatter: function () {
                    return `<span>Server: <b>${x_axis?.[this.point.x] ? x_axis?.[this.point.x] : ''}</b></span><br/><span>Service: <b>${y_axis?.[this.point.y] ? y_axis?.[this.point.y] : ""}</b></span><br/><span>Memory: <b>${this.point.value} MB</b></span>`;
                },
            },
            series: [
                {
                    name: 'Usage',
                    borderWidth: 1,
                    borderColor: "#999",
                    data: series,
                    dataLabels: {
                        enabled: false,
                    },
                    colsize: 1,
                    rowsize: 1,
                    point: {
                        events: {
                            click: function () {
                                const server = x_axis[this.x];
                                const service = y_axis[this.y];
                                handleShowDetailsPopupMemory({ server, service }, "server-service", api_data);
                            }
                        }
                    }
                },
            ],
        }
    }

    const generateUptimeOptions = (x_axis, y_axis, max_data, group, api_data) => {
        const colorPalette = [
            '#1f77b4', '#ff7700ff', '#2cd62cff', '#d62728',
            '#9467bd', '#8c564b', '#e377c2', '#7f7f7f',
            '#bcbd22', '#17becf'
        ];

        const coloredSeries = y_axis?.map((seriesItem, idx) => ({
            ...seriesItem,
            color: colorPalette[idx % colorPalette.length]
        }));
        return {
            credits: { enabled: false },
            chart: {
                type: 'line',
                height: 250,
                events: {
                    render: function () {
                        const chart = this;
                        chart.xAxis[0].ticks &&
                            Object.values(chart.xAxis[0].ticks).forEach(tick => {
                                if (tick.label && !tick.label.clickAttached) {
                                    tick.label.element.style.cursor = 'pointer';
                                    const categoryName = tick.label.textStr;
                                    tick.label.element.onclick = () => {
                                        handleShowDetailsPopupUptime(categoryName, true, api_data);
                                    };
                                    tick.label.clickAttached = true;
                                }
                            });
                    }
                }
            },
            chartSize: x_axis.length > 20 ? 'large' : x_axis.length > 10 ? 'medium' : 'small',
            title: {
                text: `<span class="p-2">${group}</span>`,
                useHTML: true,
                style: {
                    fontSize: '16px',
                    color: '#333',
                    backgroundColor: "#e5e5e5",
                },
            },
            xAxis: {
                categories: x_axis,
                labels: {
                    rotation: -45,
                    align: 'right',
                },
            },
            yAxis: {
                min: 0,
                max: max_data,
                tickInterval: Math.ceil(max_data/4),
                title: {
                    text: '',
                },
            },
            plotOptions: {
                line: {
                    point: {
                        events: {
                            click: function (e) {
                                handleShowDetailsPopupUptime(e, false, api_data);
                            }
                        }
                    },
                },
            },
            tooltip: {
                formatter: function () {
                    return `<div><span style="font-size:12px;">Server: <b>${this?.series?.name} </b><span style="color:${this.color};">\u25CF</span></span><br/></div><br/> Service: <b>${this?.x}</b><br/>Uptime: <b>${this.point.y} min.</b>`;
                }
            },
            legend: {
                layout: 'horizontal',
                align: 'center',
                verticalAlign: 'bottom',
                floating: false,
                backgroundColor: 'white',
                shadow: false,
                maxHeight: 47,
                itemMarginBottom: 4,
                navigation: {
                    activeColor: '#000',
                    animation: true,
                    arrowSize: 10,
                    inactiveColor: '#ccc',
                    style: {
                        fontWeight: 'bold',
                        color: '#333',
                        fontSize: '12px'
                    }
                },
            },
            series: coloredSeries
        };
    }

    const handleShowDetailsPopupMemory = (val, key, api_data) => {
        let serviceName = '';
        let serverName = '';
        let data = []

        if (key === "server") {
            serverName = val;
            data = _.filter(api_data, { server: serverName });
        } else if (key === "service") {
            serviceName = val;
            data = _.filter(api_data, { service_name: serviceName });
        } else if (key === "server-service") {
            serviceName = val?.service;
            serverName = val?.server
            data = _.filter(api_data, { service_name: serviceName, server: serverName });
        }
        if (!_.isEmpty(data)) {
            setPopupData(data);
            setShowPopup(true);
        }
    }

    const handleShowDetailsPopupUptime = (event, isCategoryName, api_data) => {
        let serviceName = "";
        let serverName = "";
        let data = [];
        if (event?.point) {
            const clickedPoint = event?.point;
            serviceName = clickedPoint?.category
            serverName = clickedPoint?.series?.name
            data = _.filter(api_data, { service_name: serviceName, server: serverName });
        } else if (isCategoryName) {
            serviceName = event
            data = _.filter(api_data, { service_name: serviceName });
        }
        setPopupData(data);
        setShowPopup(true);
    }

    const getServerWiseData = async () => {
        try {
            const selectedProject = sessionStorage.getItem("inspectra_project") ? JSON.parse(decryptData(sessionStorage.getItem("inspectra_project"))) : {};
            if (!_.isEmpty(selectedProject)) {
                setGaugeLoader(true);
                const payload = {
                    type: 'ram',
                    project_name: selectedProject?.project_key
                }
                const response = await services.callInspectra("GET", '/elsinspectra/top', payload);
                if (response) {
                    const data = response?.data;
                    const series = [];
                    const colorPalette = ['#FF6F61', '#6B5B95', '#88B04B', '#F7CAC9', '#92A8D1', '#955251', '#B565A7', '#009B77', '#DD4124', '#45B8AC'];
                    let pane = [
                        {
                            outerRadius: '19%',
                            innerRadius: '0%',
                            backgroundColor: "white",
                            borderWidth: 1,
                            borderColor: "white"
                        },
                    ]
                    const totalRings = data.length;
                    const totalSpace = 120;
                    const ringThickness = totalSpace / (totalRings * 1.25);

                    data.forEach((item, index) => {
                        const used = item?.used_ram?.includes("G") ? item?.used_ram?.split("G")?.[0] : (item?.used_ram?.split("M")?.[0] / 1024)?.toFixed(2);
                        const total = item?.total_ram?.includes("G") ? item?.total_ram?.split("G")?.[0] : (item?.total_ram?.split("M")?.[0] / 1024)?.toFixed(2);
                        const free = item?.free_ram?.includes("G") ? item?.free_ram?.split("G")?.[0] : (item?.free_ram?.split("M")?.[0] / 1024)?.toFixed(2);

                        const innerRadius = index * ringThickness * 1.25;
                        const outerRadius = innerRadius + ringThickness;

                        pane.push({
                            outerRadius: `${outerRadius}%`,
                            innerRadius: `${innerRadius}%`,
                            backgroundColor: "#e0e0e0ff",
                            borderWidth: 1,
                            borderColor: "white"
                        });

                        series.push({
                            name: item?.server || `Server ${index + 1}`,
                            colorByPoint: false,
                            color: colorPalette[index % colorPalette.length],
                            showInLegend: true,
                            data: [{
                                color: colorPalette[index % colorPalette.length],
                                innerRadius: `${innerRadius}%`,
                                radius: `${outerRadius}%`,
                                used_ram: used,
                                total_ram: total,
                                free_ram: free,
                                y: parseFloat(((used / total) * 100).toFixed(2))
                            }]
                        });
                    });

                    setServerWiseData({ pane: pane, series: series, api_data: data });
                    setGaugeLoader(false);
                }
            }
        } catch (error) {
            console.error("Error getting Server Error Data: ", error);
            setServerWiseData({ pane: [], series: [], api_data: [] });
            setGaugeLoader(false);
        }
    }

    const serverWiseOptions = {
        credits: { enabled: false },
        chart: {
            type: 'solidgauge',
        },
        title: {
            text: 'Memory Usage by Server',
            style: {
                fontSize: '1rem',
                fontWeight: "bold"
            }
        },
        responsive: {
            rules: [{
                condition: {
                    maxWidth: 768
                },
                chartOptions: {
                    chart: {
                        height: "auto"
                    },
                    title: {
                        style: {
                            fontSize: '14px'
                        }
                    },
                    legend: {
                        layout: "horizontal",
                        itemStyle: {
                            fontSize: "10px"
                        }
                    }
                }
            }]
        },
        pane: {
            startAngle: 0,
            endAngle: 360,
            background: serverWiseData?.pane
        },
        tooltip: {
            outside: true,
            useHtml: true,
            pointFormat: `
                <span>Server: <b>{series.name}</b></span><br/><span>Used RAM: <b>{point.used_ram} GB</b> <span style="color:{point.color}">\u25CF</span></span><br/><span>Free RAM: <b>{point.free_ram} GB</b> <span style="color:{point.bgcolor}">\u25CF</span> </span>
            `,
        },
        legend: {
            enabled: true,
            layout: "horizontal",
            align: "center",
            verticalAlign: "bottom",
            maxHeight: 60,
            navigation: {
                enabled: true,
            },
            itemStyle: {
                fontSize: '12px',
                fontWeight: 'normal',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
            },
            floating: false,
        },
        yAxis: {
            min: 0,
            max: 100,
            lineWidth: 0,
            tickPositions: []
        },
        plotOptions: {
            solidgauge: {
                dataLabels: {
                    enabled: false
                },
                stickyTracking: false,
                rounded: false,
                point: {
                    events: {
                        click: function (e) {
                            handleShowPopupRingChart(e);
                        }
                    }
                },
                events: {
                    legendItemClick: function () {
                        handleShowPopupRingChart(this);
                        if (!this.visible) {
                            this.setVisible(true, false);
                        }
                        return false;
                    }
                }
            },
        },
        series: serverWiseData?.series,
    }

    const handleShowPopupRingChart = (event) => {
        let data = [];
        if (event?.point) {
            const clickedPoint = event?.point;
            const serverName = clickedPoint?.series?.name
            data = _.filter(serverWiseData?.api_data, { server: serverName });
        } else if (event?.userOptions?.name) {
            const serverName = event?.userOptions?.name
            data = _.filter(serverWiseData?.api_data, { server: serverName });
        }
        setServerWisePopup({ show: true, data: data })
    }

    const handleApplyFilters = () => {
        const filters = {
            server: filterValues?.server || [],
            service: filterValues?.service || []
        }
        const sessionKey = widget === "memory" ? "memoryfilters" : "uptimefilters";
        sessionStorage.setItem(sessionKey, encryptData(JSON.stringify(filters)));

        // set count of filters applied
        let count = 0;
        Object.keys(filterValues)?.forEach((item) => {
            if (filterValues[item].length > 0) {
                count++;
            }
        })
        setFilterCount(count);
        getData();
        setIntervalFlag(!intervalFlag);
        setShowFilter(false);
    }

    return useMemo(() => ({
        filterCount, loader, showFilter, setShowFilter, filterValues, setFilterValues, showPopup, setShowPopup, popupData, expand, setExpand, allMemoryOptions, allUptimeOptions, serverWiseOptions, serverWisePopup, setServerWisePopup, serverWiseData, gaugeLoader, handleApplyFilters
    }), [filterCount, loader, showFilter, setShowFilter, filterValues, setFilterValues, showPopup, setShowPopup, popupData, expand, setExpand, allMemoryOptions, allUptimeOptions, serverWiseOptions, serverWisePopup, setServerWisePopup, serverWiseData, gaugeLoader, handleApplyFilters])
}